import { CustomElement } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { MARGIN, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { KeyboardInput } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { CjsInputTextEditor } from "@cervval.js/client-html5-ui/src/editors/inputTextEditor/inputTextEditor";
import { AuthErrorCode } from "@cervval.js/common-auth/src/auth.errors";
import { ERROR_MESSAGES } from "@cervval.js/common-auth/src/auth.errors.i18n";
import { APP_SERVICE, AppService, ERROR_MESSAGE_SERVICE, ErrorMessageService, LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { ErrorWithCode } from "@cervval.js/common/src/errors/errors";
import { Environment } from "@cervval.js/common/src/services/app.service";
import { I18nType, i18n } from "./i18n/i18n";

// TODO have a look to https://web.dev/sign-in-form-best-practices/

export type LoginCallback = (login: string, password: string) => Promise<void>;
export type LinkOptions = {
    text?: string,
    tooltip?: string,
    iconCss?: string[],
    css?: string[],
    callback: () => Promise<void>
};

export type LoginSettings = {
    login?: LoginCallback,
    ssoOptions?: LinkOptions[],
    customLinks?: LinkOptions[],
    appLogoUrl?: string,
    appName?: string
}

@CustomElement({
    name: "cjs-login",
    style: require("./login.component.scss"),
    template: require("./login.component.html"),
    i18n
})
export class CjsLogin extends WebComponent<I18nType> {

    /** components */
    @TypedHtmlElements({ ensureLoadedClasses: [CjsInputTextEditor] })
    public readonly components!: {
        loginContainer: HTMLDivElement,
        appName: HTMLDivElement,
        appLogo: HTMLImageElement,
        loginForm: HTMLFormElement,
        loginInput: HTMLInputElement,
        passwordInput: HTMLInputElement,
        showPasswordToggle: HTMLSpanElement,
        loginButton: HTMLButtonElement,
        message: HTMLDivElement,
        ssoOptionsContainer: HTMLDivElement,
        ssoOptions: HTMLDivElement,
        customLinks: HTMLDivElement
    };

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    @Injected(APP_SERVICE, { optional: true })
    protected readonly _app?: AppService;

    @Injected(ERROR_MESSAGE_SERVICE)
    protected readonly _errorMessageService!: ErrorMessageService;

    protected _loginInProgress = false;

    constructor(protected _settings: LoginSettings) {
        super();
        this._errorMessageService.registerErrorMessages(ERROR_MESSAGES);
    }

    /**
     * Disable login button if there is no login or password in the inputs and there is no login in progress
     */
    private _updateLoginButtonState(): void {
        const canConnect = !!this.components.loginInput.value && (!!this.components.passwordInput.value || this._app?.env === Environment.DEV);
        this.components.loginButton.disabled = !canConnect || this._loginInProgress;
    }

    /** Init component display */
    public override init(): void {
        if (this._settings.appName) {
            this.components.appName.innerText = this._settings.appName;
            this.components.appName.hidden = false;
        }

        if (this._settings.appLogoUrl) {
            this.components.appLogo.setAttribute("src", this._settings.appLogoUrl);
            this.components.appLogo.hidden = false;
        }

        if (this._settings.login) {
            // init and show login form
            this.components.loginButton.onclick = () => this._login();
            this.components.loginInput.placeholder = this.i18n.loginInput.placeholder;
            this.components.passwordInput.placeholder = this.i18n.passwordInput.placeholder;
            this.components.loginButton.innerHTML = this.i18n.loginButton.innerHTML;

            // disable loginButton until login and password contain both a value
            const loginInputModificationCallback = (): void => this._updateLoginButtonState();
            this.components.loginInput.oninput = loginInputModificationCallback;
            this.components.passwordInput.oninput = loginInputModificationCallback;

            // login on user pressing ENTER
            this.components.loginInput.addEventListener("keyup", ({ key }) => {
                if (key === KeyboardInput.ENTER) {
                    if (this.components.passwordInput.value.length === 0) {
                        // give focus to password input
                        this.components.passwordInput.focus();
                    } else if (!this.components.loginButton.disabled) {
                        // try to login
                        this._login();
                    }
                }
            });
            this.components.passwordInput.addEventListener("keyup", ({ key }) => {
                if (!this.components.loginButton.disabled && key === KeyboardInput.ENTER) {
                    this._login();
                }
            });

            // display login form
            this.components.loginForm.hidden = false;

            // handle show password
            this.components.showPasswordToggle.onclick = () => {
                if (this.components.passwordInput.type === "password") {
                    this.components.showPasswordToggle.classList.add("fa-eye-slash");
                    this.components.showPasswordToggle.classList.remove("fa-eye");
                    this.components.passwordInput.type = "text";
                } else {
                    this.components.showPasswordToggle.classList.add("fa-eye");
                    this.components.showPasswordToggle.classList.remove("fa-eye-slash");
                    this.components.passwordInput.type = "password";
                }
            };
        }

        if (this._settings.ssoOptions) {
            for (const sso of this._settings.ssoOptions) {
                this.addSSO(sso);
            }
        }

        if (this._settings.customLinks) {
            this.components.customLinks.hidden = false;
            for (const l of this._settings.customLinks) {
                this.addCustomLink(l);
            }
        }

        const searchParams = new URLSearchParams(window.location.search);
        const error = searchParams.get("error");
        if (error && Number(error) in AuthErrorCode) {
            this._showError({ code: Number(error) });
        }

        this._updateLoginButtonState();
    }

    /**
     * @returns a HTMLAnchorElement corresponding to the provided LinkOptions
     */
    protected _buildLink(option: LinkOptions): HTMLAnchorElement {
        const a: HTMLAnchorElement = document.createElement("a");
        a.setAttribute("role", "button");
        if (option.css) {
            a.classList.add(...option.css);
        }
        if (option.text) {
            a.innerText = option.text;
        }
        if (option.tooltip) {
            a.title = option.tooltip;
        }
        if (option.iconCss) {
            const icon: HTMLSpanElement = document.createElement("span");
            icon.classList.add(MARGIN.M_1);// margin
            icon.classList.add(...option.iconCss);
            a.prepend(icon);
        }
        a.tabIndex = 0;
        a.onclick = () => {
            this._ssoOptionsLinkBehavior(option);
        };
        a.addEventListener("keydown", (e) => {
            if (e.key === KeyboardInput.ENTER) {
                this._ssoOptionsLinkBehavior(option);
            }
        });

        return a;
    }

    /** Action when we click on SSOOptions link */
    private _ssoOptionsLinkBehavior(option: LinkOptions): void {
        this._clearMessage();
        option.callback().catch(error => this._showError(error));
    }

    /**
     * Will add a custom link to the login page
     */
    public addCustomLink(option: LinkOptions): void {
        const link = this._buildLink(option);
        link.classList.add("custom-link");

        this.components.customLinks.append(link);
        this.components.customLinks.hidden = false;
    }

    /**
     * Will add a one click connect button to the login page
     */
    public addSSO(option: LinkOptions): void {
        const ssoButton = this._buildLink(option);
        ssoButton.classList.add("btn", "btn-secondary", W_100);

        this.components.ssoOptions.append(ssoButton);
        this.components.ssoOptionsContainer.hidden = false;
    }

    /**
     * Will call the login method with the form parameters
     * @returns promise of login method
     */
    private _login(): void {
        if (!this._settings.login) {
            return;
        }
        this._clearMessage();

        const login = this.components.loginInput.value;
        const password = this.components.passwordInput.value;

        if (!login || !password && this._app?.env !== Environment.DEV) {
            this._showError({ code: AuthErrorCode.WRONG_LOGIN_PASSWORD });
            this._updateLoginButtonState();
            this.components.passwordInput.value = "";
            return;
        }
        this._loginInProgress = true;
        this._updateLoginButtonState();

        this._showInfo(this.i18n.loginInProgress);
        this._settings.login!(login, password).catch(error => {
            this._showError(error);
            this.components.passwordInput.value = "";
        }).then(() => {
            this._loginInProgress = false;
            this._updateLoginButtonState();
        });// FIXME handleUnexpectedError (when HandleUnexpectedErrorService merged)
    }

    /**
     * Clear the visible error
     */
    private _clearMessage(): void {
        this.components.message.innerText = "";
        this.components.message.classList.remove("visible");
    }

    /**
     * Display message in components.message
     */
    private _showInfo(info: string): void {
        this.components.message.innerText = info;
        this.components.message.classList.add("info");
        this.components.message.classList.remove("error");
        this.components.message.classList.add("visible");
    }

    /**
     * Display error in components.message
     */
    private _showError(error: ErrorWithCode): Promise<void> {
        return this._errorMessageService.getErrorMessage(error).then((errorMessage) => {
            if (errorMessage) {
                this.components.message.innerText = errorMessage;
                this.components.message.classList.remove("info");
                this.components.message.classList.add("error");
                this.components.message.classList.add("visible");
            }
        });
    }
}